/* ======================================================================== *
 * PLACEHOLDER COPY MUST NEVER REACH A COMPOSER.
 *
 * Brian: "Placeholder copy must never reach a composer — template text has
 * reached the email composer before. Guard it."
 *
 * It has, twice, and outreach.js documents both: a season-story placeholder,
 * and a generated "sequence" whose body field held a note to a copywriter —
 *
 *   "Draft — warm, human-voice congratulations to {who}, and an intro to their
 *    championship ring proof. Review and personalize before sending."
 *
 * ⚠️ WHY A DETECTOR IN PRE-FLIGHT WAS NOT ENOUGH, AND THIS EXISTS.
 *
 * outreach-preflight.js already had `findUnresolvedTokens`, and it would have
 * caught that text. It never ran on it, because pre-flight runs at APPROVE and
 * the placeholder was substituted at STAGE — so the copy a human reviewed was
 * clean, the copy that got stored was not, and the check sat downstream of the
 * substitution that caused the problem. A guard placed after the damage reads
 * exactly like a guard.
 *
 * So there is now ONE detector, exported from here, and it is called at EVERY
 * boundary where copy moves:
 *   · outreach.js  stageCampaign   — before the draft is written to app_kv
 *   · gmail-draft.js createRepDraft — immediately before the MIME is built
 *   · outreach-preflight.js         — where it always was, now importing this
 * The last one is a backstop, not the guard. The guard is the first two.
 * ======================================================================== */

const S = (v) => String(v ?? '').trim()
export const COPY_IDENTITY_FIELDS=Object.freeze(['current_champion','sr_winning_school','championship_name','champion_title_line','governing_body','champion_head_coach'])
export function displayedContactNames(name,{coach=false}={}){
 const full=typeof name==='string'?name.trim().replace(/\s+/g,' '):''
 if(!full||/[\r\n]/.test(name))return []
 return [full,`${full},`,...(coach?[`Coach ${full.split(/\s+/).pop()},`]:[])]
}
export function copyIdentityNames(record={},user={}){
 return [...COPY_IDENTITY_FIELDS.map(key=>record?.[key]),...displayedContactNames(record?.champion_head_coach,{coach:true}),user?.name?`${user.name}\nSouthern Recognition`:null].filter(value=>typeof value==='string'&&value.trim())
}
const slotWords=new Set('your our their the verified personalized personalised current school bowl artwork coach recipient rep sender team organization organisation org customer contact event championship name first last full email phone number title date year season time link url attachment image design concept address signature location city state logo here'.split(' '))
function specificationSlots(text,identityNames){
 const out=[]
 for(const match of text.matchAll(/\[([^\]\r\n[]{1,100})\]/g)){
  const label=match[1].trim()
  // Transport qualifications, numbered citations and sourced bracketed proper
  // names are labels. A string made only of slot vocabulary is an instruction.
  if(/^(?:NON-FINAL ARTWORK|ARTWORK PREVIEW|\d+(?:[, -]\d+)*|(?:figure|table|section)\s+[A-Z0-9.]+)$/i.test(label))continue
  if(label.split(/\s+/).length>1&&identityNames.includes(label))continue
  const words=label.toLowerCase().replace(/[_#-]/g,' ').split(/\s+/).filter(Boolean)
  if(/^(?:insert|add|provide|replace)\b/i.test(label)||(words.length&&words.every(word=>slotWords.has(word))))out.push({rule:'specification-token',match:match[0],say:`replace the specification placeholder with verified content: ${match[0]}`})
 }
 return out
}

/* Each rule says what it found in words a rep can act on. An id alone sends
 * somebody to read this file; "step 2 still says {{school}}" does not. */
export const COPY_RULES = Object.freeze([
  { id:'weak-outreach', re:/\b(?:just checking in|touching base|just circling back|I would like to discuss|worth a conversation)\b/gi,
    say:(m) => `replace weak outreach with a specific reason and one response ask: "${m}"` },
  { id:'banned-outreach-word', re:/\b(?:signature|victory|evolve)\b/gi,
    say:(m) => `this word is not approved for customer copy: "${m}"` },
  { id:'em-dash', re:/\u2014/g,
    say:() => 'use sentence punctuation without an em dash' },
  { id:'specification-token', re:/\[(?:rep\s*#|personalized\s+artwork|verified\s+bowl\s+name|(?:first|last|coach|school|team|bowl|rep|sender|customer|contact|event)[ _-]*(?:name|email|phone|number)|insert\b[^\]\n]{0,60})\]/gi,
    say:(m) => `replace the specification placeholder with verified content: ${m}` },
  { id:'unresolved-token', re:/\{\{[^}]*\}\}|\{[^}\n]{1,60}\}|%[A-Z_]{2,}%|\[\[[^\]]*\]\]|<[A-Z_]{2,}>/g,
    say:(m) => `an unfilled placeholder is still in the copy: ${m}` },
  /* The exact shape of the bug that shipped: instruction-to-a-writer sitting in
   * a body field. It reads like prose, which is why it survived a review. */
  { id:'instruction-text', re:/\b(review and personali[sz]e|personali[sz]e before sending|draft\s*[—–-]\s|insert\s+\w+\s+here|add\s+(?:the\s+)?\w+\s+here|lorem ipsum|TBD|TODO|FIXME|XXXX+|placeholder)\b/gi,
    say:(m) => `this is a note to whoever writes the copy, not copy: "${m}"` },
  { id:'literal-undefined', re:/\b(undefined|null|NaN)\b/g,
    say:(m) => `a value failed to resolve and printed as "${m}"` },
  /* Pre-flight's own long-standing rule: the generic fallback proved the school
   * name never filled in. Kept, because it is still the cheapest tell. */
  { id:'unfilled-school-fallback', re:/\bthe program\b/gi,
    say:() => 'the school name never filled in — the copy says "the program"' },
  /* ⚠️ NARROWER THAN PRE-FLIGHT'S VERSION, ON PURPOSE. That one also flagged
   * `congrats[!.]` and `congratulations[!.]`, which are ordinary sentences in
   * exactly the copy this feature exists to write — "Congratulations! What a
   * season." A guard that fires on correct copy gets switched off, and then it
   * is not guarding the case it was built for. Anchored to line start so
   * "Hi Coach Smith," passes and a bare "Hi," does not. */
  { id:'empty-salutation', re:/^\s*(hi|hello|dear|congratulations to)\s*[,!.]/gim,
    say:(m) => `a salutation with nobody in it: "${m.trim()}"` },
  /* A subject line that is still the internal label. Reps have sent these. */
  { id:'internal-label', re:/\b(champion-to-close|CAMP-\d+|outreach\.(?:staged|approved)|step \d+ of \d+)\b/gi,
    say:(m) => `an internal label leaked into customer-facing copy: "${m}"` },
])

/**
 * Scan one block of text. Returns [{ rule, match, say }] — empty means clean.
 * Pure and synchronous: it is called on the hot path of every stage and every
 * draft, and a guard nobody can afford to run is a guard that gets skipped.
 */
export function scanCopy(text,{identityNames=[]}={}){
  const s = String(text ?? '')
  if(!s) return []
  const out = specificationSlots(s,identityNames)
  // Context comes from current authoritative records, never caller copy fields.
  // Mask only exact identity spans for the marketing-word rule. Other uses of
  // the same word remain violations, and all placeholder rules still apply.
  let marketing=s
  for(const name of [...identityNames].filter(n=>typeof n==='string'&&n.trim()).sort((a,b)=>b.length-a.length))marketing=marketing.split(name).join(' '.repeat(name.length))
  for(const rule of COPY_RULES){
    const re = new RegExp(rule.re.source, rule.re.flags)
    let m
    while((m = re.exec(rule.id==='banned-outreach-word'?marketing:s))){
      out.push({ rule:rule.id, match:m[0], say:rule.say(m[0]) })
      if(!re.global) break
      if(m.index === re.lastIndex) re.lastIndex++ // zero-width safety
    }
  }
  return out
}

/** Empty string / whitespace-only is its own finding, not a clean pass. */
function emptiness(field, value){
  return S(value) ? null : { rule:'empty-field', match:field, say:`${field} is empty, so there is nothing for the rep to review` }
}

/**
 * THE GUARD. Throws a 422 carrying every finding rather than returning a
 * boolean, because the caller's only correct response is to refuse — and a
 * boolean invites an `if` somebody eventually inverts.
 *
 * @param parts {{subject, body, footer?}} and `where` names the boundary so the
 *        error says which one refused.
 */
export function guardCopy(parts = {}, where = 'composer', context = {}){
  const findings = [
    emptiness('subject', parts.subject),
    emptiness('body', parts.body),
    ...scanCopy(parts.subject,context).map((f) => ({ ...f, field:'subject' })),
    ...scanCopy(parts.body,context).map((f) => ({ ...f, field:'body' })),
    ...scanCopy(parts.footer,context).map((f) => ({ ...f, field:'footer' })),
  ].filter(Boolean)
  if(!findings.length) return { ok:true, findings:[] }
  throw Object.assign(new Error('placeholder_copy_refused'), { status:422,
    body:{ code:'placeholder_copy_refused', where,
      detail:`This copy is not finished, so it was not ${where === 'gmail-draft' ? 'put in a mailbox' : 'staged'}: ` +
        findings.map((f) => `${f.field || 'copy'} — ${f.say}`).join('; '),
      findings } })
}

export default { COPY_RULES, scanCopy, guardCopy }
